import {
  HighlightsController,
  type HighlightsControllerOptions
} from '@databases/highlights';

import {
  getTimelinePinnedHighlightColumnCount,
  subscribeToTimelineHighlightsGeometryChanges,
  TimelineHighlightsGeometryResolver,
  TimelinePinnedHighlightsGeometryResolver
} from './timeline-highlights-geometry.ts';
import { createTimelineHighlightsRenderingStrategy } from './timeline-highlights-rendering-strategy.ts';

class TimelineHighlightsController extends HighlightsController {
  readonly #pinnedContainer: HTMLElement;
  readonly #unsubscribeGeometryChanges: VoidFunction;

  constructor(options: Pick<HighlightsControllerOptions, 'id'>) {
    const scrollingResolver = new TimelineHighlightsGeometryResolver();
    const pinnedResolver = new TimelinePinnedHighlightsGeometryResolver();
    const pinnedContainer = document.createElement('div');

    pinnedContainer.dataset.highlightSurface = 'timeline-pinned';
    pinnedContainer.style.position = 'absolute';
    pinnedContainer.style.top = '0';
    pinnedContainer.style.left = '0';
    pinnedContainer.style.pointerEvents = 'none';

    super(scrollingResolver, {
      ...options,
      createRenderingStrategy: ({ container }) =>
        createTimelineHighlightsRenderingStrategy({
          scrollingContainer: container,
          scrollingResolver,
          pinnedContainer,
          pinnedResolver,
          getPinnedColumnCount: getTimelinePinnedHighlightColumnCount
        })
    });

    this.#pinnedContainer = pinnedContainer;

    this.#unsubscribeGeometryChanges = subscribeToTimelineHighlightsGeometryChanges(
      (adapter) => {
        if (adapter) {
          this.mount(adapter.getResizeObserveTarget());
        }

        const pinnedRoot = adapter?.getPinnedRoot();

        if (pinnedRoot && this.#pinnedContainer.parentElement !== pinnedRoot) {
          pinnedRoot.appendChild(this.#pinnedContainer);
        } else if (!pinnedRoot) {
          this.#pinnedContainer.remove();
        }

        this.redrawAll();
      }
    );
  }

  public override dispose(): void {
    this.#unsubscribeGeometryChanges();
    this.#pinnedContainer.remove();
    super.dispose();
  }
}

export function createTimelineHighlightsController(
  options: Pick<HighlightsControllerOptions, 'id'>
): HighlightsController {
  return new TimelineHighlightsController(options);
}
